(() => {
  if (customElements.get('abok-tabs')) return;

  class AbokTabs extends HTMLElement {
    constructor() {
      super();
      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
    }

    connectedCallback() {
      this.tabs = this.getOwnedElements('[data-abok-tab]');
      this.panels = this.getOwnedElements('[data-abok-tab-panel]');
      this.panelById = new Map(this.panels.map((panel) => [panel.id, panel]));

      if (!this.tabs.length || !this.panels.length) return;

      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('keydown', this.handleKeydown);
      this.addEventListener('click', this.handleClick);
      this.addEventListener('keydown', this.handleKeydown);

      const selectedIndex = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      this.activate(selectedIndex >= 0 ? selectedIndex : 0, false);
      this.setAttribute('data-tabs-ready', '');
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('keydown', this.handleKeydown);
    }

    getOwnedElements(selector) {
      return Array.from(this.querySelectorAll(selector)).filter((element) => element.closest('abok-tabs') === this);
    }

    handleClick(event) {
      const tab = event.target.closest('[data-abok-tab]');
      if (!tab || tab.closest('abok-tabs') !== this) return;

      const index = this.tabs.indexOf(tab);
      if (index >= 0) this.activate(index, false);
    }

    handleKeydown(event) {
      const tab = event.target.closest('[data-abok-tab]');
      if (!tab || tab.closest('abok-tabs') !== this) return;

      const currentIndex = this.tabs.indexOf(tab);
      const orientation = tab.closest('[role="tablist"]')?.getAttribute('aria-orientation') || 'horizontal';
      const isRtl = getComputedStyle(this).direction === 'rtl';
      let nextIndex = null;

      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.tabs.length - 1;

      if (orientation === 'vertical') {
        if (event.key === 'ArrowUp') nextIndex = currentIndex - 1;
        if (event.key === 'ArrowDown') nextIndex = currentIndex + 1;
      } else {
        if (event.key === 'ArrowLeft') nextIndex = currentIndex + (isRtl ? 1 : -1);
        if (event.key === 'ArrowRight') nextIndex = currentIndex + (isRtl ? -1 : 1);
      }

      if (nextIndex !== null) {
        event.preventDefault();
        const wrappedIndex = (nextIndex + this.tabs.length) % this.tabs.length;
        this.tabs[wrappedIndex].focus();
        this.tabs[wrappedIndex].scrollIntoView({ block: 'nearest', inline: 'nearest' });

        if (this.dataset.activation !== 'manual') this.activate(wrappedIndex, false);
        return;
      }

      if ((event.key === 'Enter' || event.key === ' ') && this.dataset.activation === 'manual') {
        event.preventDefault();
        this.activate(currentIndex, false);
      }
    }

    activate(index, shouldFocus = true) {
      const selectedTab = this.tabs[index];
      if (!selectedTab) return;

      this.tabs.forEach((tab, tabIndex) => {
        const isSelected = tabIndex === index;
        tab.setAttribute('aria-selected', String(isSelected));
        tab.tabIndex = isSelected ? 0 : -1;

        const panel = this.panelById.get(tab.getAttribute('aria-controls')) || this.panels[tabIndex];
        if (panel) panel.hidden = !isSelected;
      });

      if (shouldFocus) selectedTab.focus();
    }
  }

  customElements.define('abok-tabs', AbokTabs);
})();
